import { graphql, useFragment } from "react-relay";
import { ReactElement, useCallback, useRef, useState } from "react";
import {
  FlatList,
  ListRenderItemInfo,
  NativeScrollEvent,
  NativeSyntheticEvent,
  View,
} from "react-native";
import { WidgetNewsfeedPost } from "./WidgetNewsfeedPost";
import {
  AHIcon,
  AHPageIndicator,
  AHText,
  Box,
  TouchableBox,
  useLayoutContext,
  useScaleFactor,
} from "arkh";
import { BaseWidgetProps } from "../BaseProps";
import { NewsfeedNative_widgetNewsfeed$key } from "./__generated__/NewsfeedNative_widgetNewsfeed.graphql";
import { FormattedMessage } from "react-intl";

type Props = {
  widget: NewsfeedNative_widgetNewsfeed$key;
  mode?: "FOCUS" | "IDLE";
  interactive?: undefined | false;
} & BaseWidgetProps;

export const NewsfeedNative = (props: Props) => {
  const widget = useFragment<NewsfeedNative_widgetNewsfeed$key>(
    graphql`
      fragment NewsfeedNative_widgetNewsfeed on WidgetNewsfeed {
        id
        title
        posts {
          ...WidgetNewsfeedPost_widgetNewsfeedPost
        }
      }
    `,
    props.widget
  );

  const [height, setHeight] = useState(0);
  const [currentItem, setCurrentItem] = useState(0);

  const onScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      setCurrentItem(Math.round(e.nativeEvent.contentOffset.y / height));
    },
    [setCurrentItem, height]
  );

  type Post = (typeof widget.posts)[number];
  const renderItem = useCallback(
    ({ item }: ListRenderItemInfo<Post>) => {
      return (
        <WidgetNewsfeedPost
          post={item}
          height={height}
          ImageWrapper={ImagesWrapper}
        />
      );
    },
    [height]
  );

  const getItemLayout = useCallback(
    (_: unknown, index: number) => ({
      length: height,
      offset: index * height,
      index,
    }),
    [height]
  );

  const sf = useScaleFactor();
  const layout = useLayoutContext();

  return (
    <View
      onLayout={(e) => setHeight(e.nativeEvent.layout.height)}
      style={{ flex: 1, position: "relative" }}
    >
      <FlatList
        pagingEnabled
        data={widget.posts}
        renderItem={renderItem}
        getItemLayout={getItemLayout}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={onScroll}
        ListEmptyComponent={
          <AHText
            type="info-small"
            style={{ textAlign: "center" }}
            space="large"
          >
            <FormattedMessage
              description="WidgetNewsfeed empty"
              defaultMessage="There is nothing to show here right now."
            />
          </AHText>
        }
      />
      <View
        style={{
          position: "absolute",
          right: (-sf * layout.space.medium) / 2,
          top: 0,
          height: "100%",
          justifyContent: "center",
        }}
      >
        <View>
          <AHPageIndicator
            numberOfItems={widget.posts.length}
            currentIndex={currentItem}
            direction="vertical"
          />
        </View>
      </View>
    </View>
  );
};

const ImagesWrapper = (props: { children: ReactElement[] }) => {
  const [imageViewWidth, setImageViewWidth] = useState(0);

  const renderItem = useCallback(
    ({ item }: { item: ReactElement }) => {
      return (
        <View style={{ width: imageViewWidth, height: "100%" }}>{item}</View>
      );
    },
    [imageViewWidth]
  );

  const scrollRef = useRef<FlatList>(null);

  const [currentImage, setCurrentImage] = useState(0);
  const scrollToImage = useCallback(
    (index: number) => {
      setCurrentImage(index);
      scrollRef.current?.scrollToIndex({
        index,
        animated: true,
      });
    },
    [setCurrentImage]
  );

  const childrenLength = props.children.length;
  const prevImage = useCallback(
    () => scrollToImage((currentImage + childrenLength - 1) % childrenLength),
    [currentImage, scrollToImage, childrenLength]
  );
  const nextImage = useCallback(
    () => scrollToImage((currentImage + 1) % childrenLength),
    [currentImage, scrollToImage, childrenLength]
  );

  const layout = useLayoutContext();

  return (
    <View
      style={{ width: "100%", height: "100%", position: "relative" }}
      onLayout={(e) => setImageViewWidth(e.nativeEvent.layout.width)}
    >
      <FlatList
        ref={scrollRef}
        scrollEnabled={false}
        horizontal
        data={props.children}
        renderItem={renderItem}
      />
      {props.children.length > 1 && (
        <View
          style={{
            position: "absolute",
            left: 0,
            width: "100%",
            top: "50%",
            height: 0,
            backgroundColor: "#ff0000",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <TouchableBox
            style={{
              height: layout.icon["xlarge"],
              alignItems: "center",
              justifyContent: "center",
            }}
            color="surface"
            cornerRadius="medium"
            onPress={prevImage}
          >
            <AHIcon type="chevron-left" size="xlarge" color="washedText" />
          </TouchableBox>
          <TouchableBox
            style={{
              height: layout.icon["xlarge"],
              alignItems: "center",
              justifyContent: "center",
            }}
            color="surface"
            cornerRadius="medium"
            onPress={nextImage}
          >
            <AHIcon type="chevron-right" size="xlarge" color="washedText" />
          </TouchableBox>
        </View>
      )}
    </View>
  );
};
